import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { cloneArray } from "../../utils/undoSnapshots";
import { seededRng } from "../../utils/symbolGenerator";

/** Uniform [0, 1) from (seed, purpose, nodeId) - avoids biased djb2+modulo for short seeds. */
function unitFromSeedNode(
    seed: string,
    purpose: string,
    nodeId: string,
): number {
    return seededRng(`${seed}\0${purpose}\0${nodeId}`)();
}

export interface RectNode {
    id: string;
    x: number;
    y: number;
}

export interface RectConnection {
    from: string;
    to: string;
}

export type RectConnectionLinkMode = "proximity" | "insertion_order";

interface RectNodeConnectionsState {
    nodes: RectNode[];
    connections: RectConnection[];
    connectionLinkMode: RectConnectionLinkMode;
    connectionDistanceThreshold: number;
    brushSize: number;
    brushDensity: number;
    nodeBaseSize: number;
    nodeSizeRandomness: number;
    /** 0 = square nodes; 1 = width:height aspect ratio varies (log-uniform, deterministic per node). */
    nodeWidthHeightRandomness: number;
    nodeColor: string;
    nodeFillEnabled: boolean;
    strokeColor: string;
    connectionOpacity: number;
    /** Drives brush scatter and per-node size/aspect variation. */
    seed: string;
    viewBoxAttr: string;
    addNode: (x: number, y: number) => void;
    clearNodes: () => void;
    setConnectionLinkMode: (mode: RectConnectionLinkMode) => void;
    setConnectionDistanceThreshold: (threshold: number) => void;
    setBrushSize: (size: number) => void;
    setBrushDensity: (density: number) => void;
    setNodeBaseSize: (size: number) => void;
    setNodeSizeRandomness: (randomness: number) => void;
    setNodeWidthHeightRandomness: (ratio: number) => void;
    setNodeColor: (color: string) => void;
    setNodeFillEnabled: (enabled: boolean) => void;
    setStrokeColor: (color: string) => void;
    setConnectionOpacity: (opacity: number) => void;
    setSeed: (seed: string) => void;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    getNodeRectHalfExtents: (nodeId: string) => { hw: number; hh: number };
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const RectNodeConnectionsContext =
    createContext<RectNodeConnectionsState | null>(null);

export function useRectNodeConnections() {
    const context = useContext(RectNodeConnectionsContext);
    if (!context) {
        throw new Error(
            "useRectNodeConnections must be used within RectNodeConnectionsProvider",
        );
    }
    return context;
}

interface RectNodeConnectionsProviderProps {
    children: React.ReactNode;
    initialConnectionLinkMode?: RectConnectionLinkMode;
    initialConnectionDistanceThreshold?: number;
    initialBrushSize?: number;
    initialBrushDensity?: number;
    initialNodeBaseSize?: number;
    initialNodeSizeRandomness?: number;
    initialNodeWidthHeightRandomness?: number;
    initialNodeColor?: string;
    initialNodeFillEnabled?: boolean;
    initialStrokeColor?: string;
    initialConnectionOpacity?: number;
    initialSeed?: string;
}

export function RectNodeConnectionsProvider({
    children,
    initialConnectionLinkMode = "proximity",
    initialConnectionDistanceThreshold = 500,
    initialBrushSize = 0,
    initialBrushDensity = 1,
    initialNodeBaseSize = 12,
    initialNodeSizeRandomness = 0,
    initialNodeWidthHeightRandomness = 0,
    initialNodeColor = "#ffffff",
    initialNodeFillEnabled = true,
    initialStrokeColor = "#4a9eff",
    initialConnectionOpacity = 1,
    initialSeed = "1",
}: RectNodeConnectionsProviderProps) {
    const [nodes, setNodes] = useState<RectNode[]>([]);
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const history = useUndoRedo<RectNode[]>({ clone: cloneArray });

    const pushNodesSnapshot = useCallback(() => {
        history.push(nodesRef.current);
    }, [history]);

    const undo = useCallback(() => {
        history.undo(nodesRef.current, setNodes);
    }, [history]);

    const redo = useCallback(() => {
        history.redo(nodesRef.current, setNodes);
    }, [history]);

    const [connectionLinkMode, setConnectionLinkMode] =
        useState<RectConnectionLinkMode>(initialConnectionLinkMode);
    const [connectionDistanceThreshold, setConnectionDistanceThreshold] =
        useState(initialConnectionDistanceThreshold);
    const [brushSize, setBrushSize] = useState(initialBrushSize);
    const [brushDensity, setBrushDensity] = useState(initialBrushDensity);
    const [nodeBaseSize, setNodeBaseSize] = useState(initialNodeBaseSize);
    const [nodeSizeRandomness, setNodeSizeRandomness] = useState(
        initialNodeSizeRandomness,
    );
    const [nodeWidthHeightRandomness, setNodeWidthHeightRandomness] =
        useState(initialNodeWidthHeightRandomness);
    const [nodeColor, setNodeColor] = useState(initialNodeColor);
    const [nodeFillEnabled, setNodeFillEnabled] = useState(
        initialNodeFillEnabled,
    );
    const [strokeColor, setStrokeColor] = useState(initialStrokeColor);
    const [connectionOpacity, setConnectionOpacity] = useState(
        initialConnectionOpacity,
    );
    const [seed, setSeed] = useState(initialSeed);

    const placementRngRef = useRef(seededRng(initialSeed));
    const nodeSeqRef = useRef(0);

    useEffect(() => {
        placementRngRef.current = seededRng(seed);
    }, [seed]);

    const viewBoxAttr = "0 0 1000 1000";

    const connections = useMemo(() => {
        if (nodes.length < 2) return [];
        if (connectionLinkMode === "insertion_order") {
            const conns: RectConnection[] = [];
            for (let i = 0; i < nodes.length - 1; i++) {
                conns.push({
                    from: nodes[i].id,
                    to: nodes[i + 1].id,
                });
            }
            return conns;
        }

        const conns: RectConnection[] = [];
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const node1 = nodes[i];
                const node2 = nodes[j];
                const dx = node2.x - node1.x;
                const dy = node2.y - node1.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < connectionDistanceThreshold) {
                    conns.push({ from: node1.id, to: node2.id });
                }
            }
        }

        return conns;
    }, [nodes, connectionDistanceThreshold, connectionLinkMode]);

    const addNode = (x: number, y: number) => {
        pushNodesSnapshot();
        const rng = placementRngRef.current;

        if (brushSize <= 0 || brushDensity <= 0) {
            const newNode: RectNode = {
                id: `node-${nodeSeqRef.current++}`,
                x,
                y,
            };
            setNodes((prev) => [...prev, newNode]);
            return;
        }

        const nodeCount = Math.max(1, Math.round(brushDensity));
        const newNodes: RectNode[] = [];

        for (let i = 0; i < nodeCount; i++) {
            const angle = rng() * 2 * Math.PI;
            const radius = Math.sqrt(rng()) * brushSize;
            const nodeX = x + Math.cos(angle) * radius;
            const nodeY = y + Math.sin(angle) * radius;

            newNodes.push({
                id: `node-${nodeSeqRef.current++}`,
                x: nodeX,
                y: nodeY,
            });
        }

        setNodes((prev) => [...prev, ...newNodes]);
    };

    const clearNodes = () => {
        if (nodesRef.current.length === 0) return;
        pushNodesSnapshot();
        setNodes([]);
        placementRngRef.current = seededRng(seed);
        nodeSeqRef.current = 0;
    };

    const getNodeHalfExtent = (nodeId: string): number => {
        const baseSize = nodeBaseSize;

        if (nodeSizeRandomness <= 0) {
            return baseSize;
        }

        const u = unitFromSeedNode(seed, "size", nodeId);
        const randomValue = u * 2 - 1;
        const randomOffset = baseSize * nodeSizeRandomness * randomValue;

        return Math.max(1, baseSize + randomOffset);
    };

    const getNodeRectHalfExtents = (nodeId: string): { hw: number; hh: number } => {
        const baseHalf = getNodeHalfExtent(nodeId);
        if (nodeWidthHeightRandomness <= 0) {
            return { hw: baseHalf, hh: baseHalf };
        }
        // Sample width:height ratio in log space so rectangles are usually non-square
        // (independent jitter on each axis often landed u ~ v). Keeps hw*hh = baseHalf^2.
        const t = unitFromSeedNode(seed, "wh-aspect", nodeId);
        const maxLog2Aspect = 2.5 * nodeWidthHeightRandomness;
        const log2Aspect = (t * 2 - 1) * maxLog2Aspect;
        const aspect = Math.pow(2, log2Aspect);
        const hw = baseHalf * Math.sqrt(aspect);
        const hh = baseHalf / Math.sqrt(aspect);
        return {
            hw: Math.max(0.5, hw),
            hh: Math.max(0.5, hh),
        };
    };

    const buildExportSvgString = (): string => {
        const connectionsMarkup = connections
            .map((conn) => {
                const fromNode = nodes.find((n) => n.id === conn.from);
                const toNode = nodes.find((n) => n.id === conn.to);
                if (!fromNode || !toNode) return "";

                return `    <line x1="${fromNode.x}" y1="${fromNode.y}" x2="${toNode.x}" y2="${toNode.y}" stroke="${strokeColor}" stroke-width="2" opacity="${connectionOpacity}" />`;
            })
            .filter(Boolean)
            .join("\n");

        const nodesMarkup = nodes
            .map((node) => {
                const { hw, hh } = getNodeRectHalfExtents(node.id);
                const x = node.x - hw;
                const y = node.y - hh;
                const rw = 2 * hw;
                const rh = 2 * hh;
                const d = `M ${node.x - hw} ${node.y - hh} L ${node.x + hw} ${node.y + hh} M ${node.x + hw} ${node.y - hh} L ${node.x - hw} ${node.y + hh}`;
                const fillAttr = nodeFillEnabled ? nodeColor : "none";
                return `    <rect x="${x}" y="${y}" width="${rw}" height="${rh}" fill="${fillAttr}" stroke="${strokeColor}" stroke-width="2" />\n    <path d="${d}" fill="none" stroke="${strokeColor}" stroke-width="2" />`;
            })
            .join("\n");

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="connections">
${connectionsMarkup}
  </g>
  <g class="nodes">
${nodesMarkup}
  </g>
</svg>`;
    };

    const handleDownload = () => {
        if (nodes.length === 0) return;

        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `rect-node-connections-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            connectionLinkMode,
            connectionDistanceThreshold,
            brushSize,
            brushDensity,
            nodeBaseSize,
            nodeSizeRandomness,
            nodeWidthHeightRandomness,
            nodeColor,
            nodeFillEnabled,
            strokeColor,
            connectionOpacity,
            seed,
        }),
        [
            connectionLinkMode,
            connectionDistanceThreshold,
            brushSize,
            brushDensity,
            nodeBaseSize,
            nodeSizeRandomness,
            nodeWidthHeightRandomness,
            nodeColor,
            nodeFillEnabled,
            strokeColor,
            connectionOpacity,
            seed,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (
            values.connectionLinkMode === "proximity" ||
            values.connectionLinkMode === "insertion_order"
        ) {
            setConnectionLinkMode(values.connectionLinkMode);
        }
        if (values.connectionDistanceThreshold != null) {
            setConnectionDistanceThreshold(
                values.connectionDistanceThreshold as number,
            );
        }
        if (values.brushSize != null) setBrushSize(values.brushSize as number);
        if (values.brushDensity != null) {
            setBrushDensity(values.brushDensity as number);
        }
        if (values.nodeBaseSize != null) {
            setNodeBaseSize(values.nodeBaseSize as number);
        }
        if (values.nodeSizeRandomness != null) {
            setNodeSizeRandomness(values.nodeSizeRandomness as number);
        }
        if (values.nodeWidthHeightRandomness != null) {
            setNodeWidthHeightRandomness(
                Math.max(
                    0,
                    Math.min(1, values.nodeWidthHeightRandomness as number),
                ),
            );
        }
        if (values.nodeColor != null) setNodeColor(values.nodeColor as string);
        if (typeof values.nodeFillEnabled === "boolean") {
            setNodeFillEnabled(values.nodeFillEnabled);
        }
        if (values.strokeColor != null) {
            setStrokeColor(values.strokeColor as string);
        }
        if (values.connectionOpacity != null) {
            setConnectionOpacity(values.connectionOpacity as number);
        }
        if (values.seed != null) setSeed(String(values.seed));
    }, []);

    const { resetParameters: resetStoredParameters } = useToolParamPersistence({
        toolId: "rect-node-connections",
        defaultPreset: {
            connectionLinkMode: initialConnectionLinkMode,
            connectionDistanceThreshold: initialConnectionDistanceThreshold,
            brushSize: initialBrushSize,
            brushDensity: initialBrushDensity,
            nodeBaseSize: initialNodeBaseSize,
            nodeSizeRandomness: initialNodeSizeRandomness,
            nodeWidthHeightRandomness: initialNodeWidthHeightRandomness,
            nodeColor: initialNodeColor,
            nodeFillEnabled: initialNodeFillEnabled,
            strokeColor: initialStrokeColor,
            connectionOpacity: initialConnectionOpacity,
            seed: initialSeed,
        },
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        history.clear();
        resetStoredParameters();
    }, [history, resetStoredParameters]);

    return (
        <RectNodeConnectionsContext.Provider
            value={{
                nodes,
                connections,
                connectionLinkMode,
                connectionDistanceThreshold,
                brushSize,
                brushDensity,
                nodeBaseSize,
                nodeSizeRandomness,
                nodeWidthHeightRandomness,
                nodeColor,
                nodeFillEnabled,
                strokeColor,
                connectionOpacity,
                seed,
                viewBoxAttr,
                addNode,
                clearNodes,
                setConnectionLinkMode,
                setConnectionDistanceThreshold,
                setBrushSize,
                setBrushDensity,
                setNodeBaseSize,
                setNodeSizeRandomness,
                setNodeWidthHeightRandomness,
                setNodeColor,
                setNodeFillEnabled,
                setStrokeColor,
                setConnectionOpacity,
                setSeed,
                buildExportSvgString,
                handleDownload,
                applyPreset,
                getPresetValues,
                resetParameters,
                getNodeRectHalfExtents,
                undo,
                redo,
                canUndo: history.canUndo,
                canRedo: history.canRedo,
            }}
        >
            {children}
        </RectNodeConnectionsContext.Provider>
    );
}
